<!-- src/components/Forms.vue -->
<!--
  Staff UI for the multi-tenant bail bond system.

  Two views in one component, switched by `selectedCase`:
    - Dashboard (case list) — when selectedCase is null
    - Case Detail            — when selectedCase is set

  Click a case row → CaseDetail loads with that case. Click "Cases"
  breadcrumb in the case detail header → back to dashboard.

  Data is in-memory (FAKE_CASES) so we can iterate on UX without
  backend wiring. The shape of each case object roughly matches what
  we'll persist to Mongo later.

  Color scheme matches the existing menu: #635347 (dark brown primary),
  #d2b48c (tan accent).
-->

<template>

  <!-- Case Detail view, shown when a case is selected -->
  <CaseDetail
    v-if="selectedCase"
    :case-data="selectedCase"
    @back="selectedCase = null"
    @update="onCaseUpdate"
  />

  <!-- Dashboard view, shown by default -->
  <v-container v-else fluid class="forms-container">

    <!-- Header: title + tabs + new case button -->
    <v-row align="center" no-gutters class="header-row">
      <v-col cols="auto" class="title-col">
        <h1>Cases</h1>
      </v-col>
      <v-col>
        <v-tabs
          v-model="activeTab"
          color="#635347"
          density="comfortable"
          align-tabs="start"
        >
          <v-tab value="active">
            Active
            <span class="tab-count">{{ casesByTab.active.length }}</span>
          </v-tab>
          <v-tab value="archived">
            Archived
            <span class="tab-count">{{ casesByTab.archived.length }}</span>
          </v-tab>
        </v-tabs>
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="#635347"
          variant="flat"
          @click="onNewCaseClick"
        >
          <v-icon start>mdi-plus</v-icon>
          New Case
        </v-btn>
      </v-col>
    </v-row>

    <!-- Search field -->
    <v-row no-gutters class="search-row">
      <v-col cols="12">
        <v-text-field
          v-model="searchQuery"
          placeholder="Search by Case ID, defendant name, indemnitor name..."
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          clearable
        />
      </v-col>
    </v-row>

    <!-- Case list -->
    <v-row no-gutters>
      <v-col cols="12">

        <!-- Empty state -->
        <div v-if="visibleCases.length === 0" class="empty-state">
          <v-icon size="48" color="grey-lighten-1">mdi-folder-open-outline</v-icon>
          <div class="empty-state-text">
            <template v-if="searchQuery">
              No cases match "{{ searchQuery }}"
            </template>
            <template v-else-if="activeTab === 'active'">
              No active cases. Click "New Case" to start.
            </template>
            <template v-else-if="activeTab === 'archived'">
              No archived cases yet.
            </template>
            <template v-else>
              No deleted cases.
            </template>
          </div>
        </div>

        <!-- Case rows -->
        <v-card
          v-for="c in visibleCases"
          :key="c._id"
          class="case-card"
          :class="{
            'case-card-stuck': isStuck(c),
            'case-card-expanded': isExpanded(c),
          }"
          flat
          border
        >
          <div class="case-row" @click="toggleExpansion(c)">

            <!-- Case ID column -->
            <div class="col-id clickable-edit" @click.stop="openEdit(c)">
              <div class="id-value" :class="{ 'id-missing': !caseDisplayId(c) }">
                {{ caseDisplayId(c) || '—' }}
              </div>
              <div v-if="!caseDisplayId(c)" class="id-hint">No Case ID yet</div>
            </div>

            <!-- Participants column: defendant + indemnitor(s), stacked -->
            <div class="col-participants">
              <div class="participant-line">
                <v-tooltip text="Defendant" location="top" open-delay="200">
                  <template #activator="{ props }">
                    <v-icon
                      v-bind="props"
                      size="16"
                      class="participant-icon participant-icon-defendant"
                    >
                      mdi-account-circle
                    </v-icon>
                  </template>
                </v-tooltip>
                <span
                  class="participant-name clickable-edit"
                  @click.stop="openEdit(c)"
                >
                  {{ displayName(getDefendant(c)) || 'No defendant name' }}
                </span>
                <span
                  v-if="progressSummary(getDefendant(c))"
                  class="participant-progress clickable-progress"
                  @click.stop="toggleProgress(c, 'defendant')"
                >
                  · {{ progressSummary(getDefendant(c)) }}
                </span>
                <span
                  v-if="getDefendant(c)"
                  class="participant-contact clickable-edit"
                  :class="{ 'contact-missing': !getDefendant(c)?.identity?.phone }"
                  @click.stop="openEdit(c)"
                >
                  ·
                  <v-icon size="12" class="contact-icon">mdi-phone-outline</v-icon>
                  {{ getDefendant(c)?.identity?.phone ? formatPhoneShort(getDefendant(c)?.identity?.phone) : 'No phone' }}
                </span>
                <span
                  v-if="getDefendant(c)"
                  class="participant-contact clickable-edit"
                  :class="{ 'contact-missing': !getDefendant(c)?.identity?.email }"
                  @click.stop="openEdit(c)"
                >
                  ·
                  <v-icon size="12" class="contact-icon">mdi-email-outline</v-icon>
                  {{ getDefendant(c)?.identity?.email || 'No email' }}
                </span>
              </div>
              <ParticipantProgress
                v-if="isProgressActive(c, 'defendant')"
                :case-id="String(c._id)"
                role="defendant"
                :participant="getDefendant(c)"
              />
              <template
                v-for="(indem, idx) in getIndemnitors(c)"
                :key="'indem-line-' + idx"
              >
                <div class="participant-line">
                  <v-tooltip text="Indemnitor" location="top" open-delay="200">
                    <template #activator="{ props }">
                      <v-icon
                        v-bind="props"
                        size="16"
                        class="participant-icon participant-icon-indemnitor"
                      >
                        mdi-shield-account
                      </v-icon>
                    </template>
                  </v-tooltip>
                  <span
                    class="participant-name clickable-edit"
                    @click.stop="openEdit(c)"
                  >
                    {{ displayName(indem) || 'Unnamed indemnitor' }}
                  </span>
                  <span
                    v-if="progressSummary(indem)"
                    class="participant-progress clickable-progress"
                    @click.stop="toggleProgress(c, 'indemnitor' + (idx + 1))"
                  >
                    · {{ progressSummary(indem) }}
                  </span>
                  <span
                    class="participant-contact clickable-edit"
                    :class="{ 'contact-missing': !indem.identity?.phone }"
                    @click.stop="openEdit(c)"
                  >
                    ·
                    <v-icon size="12" class="contact-icon">mdi-phone-outline</v-icon>
                    {{ indem.identity?.phone ? formatPhoneShort(indem.identity?.phone) : 'No phone' }}
                  </span>
                  <span
                    class="participant-contact clickable-edit"
                    :class="{ 'contact-missing': !indem.identity?.email }"
                    @click.stop="openEdit(c)"
                  >
                    ·
                    <v-icon size="12" class="contact-icon">mdi-email-outline</v-icon>
                    {{ indem.identity?.email || 'No email' }}
                  </span>
                </div>
                <ParticipantProgress
                  v-if="isProgressActive(c, 'indemnitor' + (idx + 1))"
                  :case-id="String(c._id)"
                  :role="'indemnitor' + (idx + 1)"
                  :participant="indem"
                />
              </template>
              <div
                v-if="c.case_fields?.court"
                class="court-line clickable-edit"
                @click.stop="openEdit(c)"
              >
                {{ c.case_fields.court }}
              </div>
            </div>

            <!-- Bond amount column -->
            <div class="col-bond clickable-edit" @click.stop="openEdit(c)">
              <div v-if="caseBondAmount(c)" class="bond-value">
                {{ formatCurrency(caseBondAmount(c)) }}
              </div>
              <div v-else class="bond-missing">—</div>
            </div>

            <!-- Status column -->
            <div class="col-status">
              <v-chip
                :color="derivedStatus(c).color"
                size="small"
                variant="flat"
                class="status-chip"
              >
                {{ derivedStatus(c).label }}
              </v-chip>
              <div v-if="derivedStatus(c).detail" class="status-detail">
                {{ derivedStatus(c).detail }}
              </div>
            </div>

            <!-- Last activity column -->
            <div class="col-activity">
              <div class="activity-time">{{ formatTimeAgo(caseUpdatedAt(c)) }}</div>
              <div v-if="caseUnreadCount(c) > 0" class="row-unread">
                <span class="row-unread-label">
                  {{ caseUnreadCount(c) }}
                  unread {{ caseUnreadCount(c) === 1 ? 'message' : 'messages' }}
                </span>
              </div>
              <div v-if="isStuck(c)" class="stuck-indicator">
                <v-icon size="14" color="orange-darken-2">mdi-alert-circle-outline</v-icon>
                Stuck
              </div>
            </div>

            <!-- Kebab menu -->
            <div class="col-kebab" @click.stop>
              <v-menu location="bottom end">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon
                    variant="text"
                    size="x-small"
                    class="kebab-btn"
                  >
                    <v-icon size="18">mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact" class="kebab-list">
                  <v-list-item @click="onArchiveToggle(c)">
                    <v-list-item-title>
                      {{ c.status === 'archived' ? 'Unarchive' : 'Archive' }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

          </div>

          <!-- Expanded content (below the row header) -->
          <div v-if="isExpanded(c)" class="case-expanded" @click.stop>

            <!-- Participants section -->
            <div class="expanded-section">
              <ParticipantCard
                :participant="getDefendant(c)"
                role="defendant"
                @send-link-sms="onSendLink(c, 'defendant', 'sms')"
                @send-link-email="onSendLink(c, 'defendant', 'email')"
                @view-form="onParticipantAction(c, 'view_form', { role: 'defendant' })"
                @ok-to-sign="onParticipantAction(c, 'ok_to_sign', { role: 'defendant' })"
                @view-docs="onParticipantAction(c, 'view_docs', { role: 'defendant' })"
              />
              <ParticipantMessages
                v-if="getDefendant(c)"
                :ref="(el) => registerMessagesRef(getDefendant(c)?.identity?.phone, el)"
                :participant="getDefendant(c)"
                role="defendant"
                :thread="getThread(getDefendant(c)?.identity?.phone)"
                :consent="getConsent(getDefendant(c)?.identity?.phone)"
                :sending="isSending(getDefendant(c)?.identity?.phone)"
                :expanded="isMessageExpanded(c, 'defendant')"
                @toggle="toggleMessageExpansion(c, 'defendant')"
                @send="onMessageSend(getDefendant(c), $event)"
                @acknowledge="onMessageAcknowledge(getDefendant(c))"
              />
              <template v-for="(indem, idx) in getIndemnitors(c)" :key="'indem-' + idx">
                <ParticipantCard
                  :participant="indem"
                  role="indemnitor"
                  :role-suffix="getIndemnitors(c).length > 1 ? ` ${idx + 1}` : ''"
                  @send-link-sms="onSendLink(c, 'indemnitor' + (idx + 1), 'sms')"
                  @send-link-email="onSendLink(c, 'indemnitor' + (idx + 1), 'email')"
                  @view-form="onParticipantAction(c, 'view_form', { role: 'indemnitor', idx })"
                  @ok-to-sign="onParticipantAction(c, 'ok_to_sign', { role: 'indemnitor', idx })"
                  @view-docs="onParticipantAction(c, 'view_docs', { role: 'indemnitor', idx })"
                  @remove="onParticipantAction(c, 'remove_indemnitor', { idx })"
                />
                <ParticipantMessages
                  :ref="(el) => registerMessagesRef(indem.identity?.phone, el)"
                  :participant="indem"
                  role="indemnitor"
                  :role-suffix="getIndemnitors(c).length > 1 ? ` ${idx + 1}` : ''"
                  :thread="getThread(indem.identity?.phone)"
                  :consent="getConsent(indem.identity?.phone)"
                  :sending="isSending(indem.identity?.phone)"
                  :expanded="isMessageExpanded(c, 'indemnitor', idx)"
                  @toggle="toggleMessageExpansion(c, 'indemnitor', idx)"
                  @send="onMessageSend(indem, $event)"
                  @acknowledge="onMessageAcknowledge(indem)"
                />
              </template>
            </div>

          </div>

        </v-card>
      </v-col>
    </v-row>

    <!-- Case Wizard modal -->
    <CaseWizardModal
      v-model="newCaseModalOpen"
      :company-number="agencyCompanyNumber"
      :edit-case="editingCase"
      :initial-step="editingCase ? 3 : 1"
      @create="onCaseCreated"
      @save="onCaseUpdated"
    />

    <!-- Send Link dialog -->
    <v-dialog v-model="sendLinkDialogOpen" max-width="460" persistent>
      <v-card>
        <v-card-title class="dialog-title">Send Application Link</v-card-title>
        <v-card-text class="dialog-body">
          <p class="send-link-prompt">
            Send application link to
            <strong>{{ sendLinkTarget.name }}</strong>
            at <strong>{{ sendLinkTarget.contact }}</strong>?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-for="opt in linkLanguageOptions"
            :key="opt.code"
            :variant="sendLinkLocale === opt.code ? 'flat' : 'elevated'"
            :color="sendLinkLocale === opt.code ? '#635347' : undefined"
            @click="sendLinkLocale = opt.code"
            :disabled="sendLinkSubmitting"
            size="small"
            class="lang-option-btn"
          >{{ opt.label }}</v-btn>
          <v-spacer />
          <v-btn
            variant="text"
            @click="sendLinkDialogOpen = false"
            :disabled="sendLinkSubmitting"
          >Cancel</v-btn>
          <v-btn
            color="#635347"
            variant="flat"
            @click="onSendLinkConfirm"
            :loading="sendLinkSubmitting"
          >Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for case-created feedback -->
    <v-snackbar
      v-model="createdSnackbarOpen"
      :timeout="3000"
      color="#635347"
      location="bottom right"
    >
      {{ createdSnackbarText }}
      <template #actions>
        <v-btn
          variant="text"
          size="small"
          @click="createdSnackbarOpen = false"
        >
          Dismiss
        </v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>


<script>
import { mapGetters } from 'vuex';
import { auth } from '../firebase';
import { eventService } from '../services/EventService';
import CaseWizardModal from './CaseWizardModal.vue';
import CaseDetail from './CaseDetail.vue';
import ParticipantCard from './ParticipantCard.vue';
import ParticipantMessages from './ParticipantMessages.vue';
import ParticipantProgress from './ParticipantProgress.vue';

// ---------------------------------------------------------------------------
// Time helpers (used by timeAgo display, staleness checks, etc.)
// ---------------------------------------------------------------------------

const HOUR = 1000 * 60 * 60;
const DAY = HOUR * 24;
const now = () => Date.now();



// ---------------------------------------------------------------------------
// Status derivation
// ---------------------------------------------------------------------------
// Maps the case's 4-state status (draft | active | completed | archived)
// plus participant statuses into a display label, Vuetify color, and
// optional detail string. The color carries urgency information:
//
//   grey   — inert (draft with missing data)
//   amber  — agent action required (invite, review, seal)
//   blue   — participants are doing their part; agent waits
//   green  — done or close to done

function _allParticipants(c) {
  const d = c.participants?.defendant;
  const i1 = c.participants?.indemnitor1;
  const i2 = c.participants?.indemnitor2;
  return [d, i1, i2].filter(Boolean);
}

function derivedStatusFor(c) {
  switch (c.status) {

    case 'draft': {
      const d = c.participants?.defendant;
      const missing = [];
      if (!c.case_fields?.court_case_number) missing.push('Case #');
      if (!d?.identity?.first_name && !d?.identity?.last_name) missing.push('defendant name');
      if (!c.case_fields?.bond_amount_numeric) missing.push('bond amount');
      if (missing.length > 0) {
        return {
          label: 'Draft',
          color: 'grey',
          detail: `Missing: ${missing.slice(0, 2).join(', ')}` +
                  (missing.length > 2 ? '…' : ''),
        };
      }
      return { label: 'Ready to invite', color: 'amber-darken-2' };
    }

    case 'active': {
      // Derive detail from participant-level statuses.
      const all = _allParticipants(c);
      if (all.length === 0) return { label: 'Active', color: 'blue' };

      const statuses = all.map(p => p.status);
      if (statuses.every(s => s === 'awaiting_invite'))
        return { label: 'Ready to invite', color: 'amber-darken-2' };
      if (statuses.every(s => s === 'submitted'))
        return { label: 'Ready for review', color: 'amber-darken-2' };
      if (statuses.every(s => s === 'signed' || s === 'ready_to_sign'))
        return { label: 'Ready to seal', color: 'amber-darken-2' };
      if (statuses.some(s => s === 'filling' || s === 'invited'))
        return { label: 'Forms in progress', color: 'blue' };
      return { label: 'Active', color: 'blue' };
    }

    case 'completed':
      return { label: c.sealed ? 'Sealed' : 'Completed', color: 'green-darken-2' };

    case 'archived':
      return { label: 'Archived', color: 'green-darken-2' };

    default:
      return { label: c.status || '—', color: 'grey' };
  }
}


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default {
  name: 'FormsView',

  components: {
    CaseWizardModal,
    CaseDetail,
    ParticipantCard,
    ParticipantMessages,
    ParticipantProgress,
  },

  data() {
    return {
      cases: [],
      activeTab: 'active',
      searchQuery: '',
      newCaseModalOpen: false,
      // Non-null when the wizard is open in edit mode. Set by
      // openEdit() before opening the modal; cleared by
      // onNewCaseClick() before opening the modal for a new case.
      // The modal closing itself doesn't reset this — it's reset on
      // the next open, so a quick re-click on the same participant
      // re-enters edit mode cleanly.
      editingCase: null,
      createdSnackbarOpen: false,
      createdSnackbarText: '',

      // ── Send Link dialog ──────────────────────────────────────
      sendLinkDialogOpen: false,
      sendLinkLocale: 'en-US',
      sendLinkSubmitting: false,
      // { caseId, role, method, name, contact }
      sendLinkTarget: { caseId: null, role: null, method: null, name: '', contact: '' },
      selectedCase: null,
      // Set of case internalIds that are currently expanded inline in
      // the dashboard. Agents manage their own workspace — multiple
      // rows can be expanded at once.
      expandedCaseIds: new Set(),
      // Set of message-strip keys ("<caseId>:<role>[:<idx>]") that are
      // currently expanded. Independent of row expansion state.
      expandedMessageKeys: new Set(),

      // Which participant's progress panel is currently showing.
      // { caseId: string, role: string } when open, null when closed.
      // Only one progress panel is open at a time — clicking another
      // participant closes the previous one.
      activeProgress: null,

      // ── SMS state ────────────────────────────────────────────────
      // Phone-keyed thread cache. Each entry is
      // { thread: [...], loaded: boolean }. We use a plain object so
      // Vue 3's reactivity tracks property additions cleanly.
      //   - loaded=false: never fetched (GET /sms/thread hasn't run)
      //                   or fetch in flight. SSE events for this
      //                   phone are dropped until loaded (so we don't
      //                   create an orphan partial thread that GET
      //                   would later contradict).
      //   - loaded=true:  has the server's view of the thread. SSE
      //                   events for this phone are appended (with
      //                   messageSid de-dup).
      threadsByPhone: {},

      // Phone-keyed consent cache. Always returns { status: 'granted' }
      // for BBAS — SmsThreadPanel has requires-consent=false so this
      // value is effectively ignored — but we still hold it so the
      // GET /sms/thread response can be read uniformly.
      consentByPhone: {},

      // Phones currently in the middle of a send. We pass the flag
      // down to the corresponding ParticipantMessages so its send
      // button disables while in flight.
      sendingPhones: new Set(),

      // Per-participant template refs, keyed by phone. Used to call
      // clearComposer() on a successful send.
      messagesRefs: Object.create(null),

      // Bound SSE handler reference, stored so removeEventListener
      // can receive the same function on unsubscribe (otherwise the
      // remove is a no-op and the listener leaks). Avoid an underscore
      // prefix here — Vue's Options API doesn't proxy `_`/`$`-prefixed
      // data keys, and vue/no-reserved-keys correctly flags it.
      smsMessageHandler: null,

      // SSE handler ref for case-change events. Stored so we can
      // removeEventListener cleanly on unmount.
      caseEventHandler: null,
      // Debounce timer for case refetch (multiple rapid changes
      // collapse into one refetch).
      caseRefetchTimer: null,
    };
  },

  computed: {
    ...mapGetters(['getSelectedCompany']),

    // CompanyNumber of the agency the user is currently logged into.
    // Drives SSE subscription and the case-level companyNumber used
    // for SMS API calls. Distinct from agencyCompanyNumber (which is
    // pinned to 1008 for the wizard's document library) — for dev
    // convenience the user should be logged into 1008 so both line
    // up. Falls back to 1008 so dev without a populated store still
    // works.
    smsCompanyNumber() {
      return this.getSelectedCompany?.companyNumber || 1008;
    },
    // Cases bucketed by tab. Active = anything that isn't archived.
    // Archive and delete are synonymous in this system.
    casesByTab() {
      const active = [];
      const archived = [];
      for (const c of this.cases) {
        if (c.status === 'archived') archived.push(c);
        else active.push(c);
      }
      // Sort each bucket by updated_at (most recent first).
      // Dates arrive as ISO strings from the API; new Date() coercion
      // makes the subtraction work.
      const byRecent = (a, b) =>
        new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
      return {
        active: active.sort(byRecent),
        archived: archived.sort(byRecent),
      };
    },

    // Cases in the current tab, filtered by search query.
    visibleCases() {
      const inTab = this.casesByTab[this.activeTab] || [];
      const q = (this.searchQuery || '').trim().toLowerCase();
      if (!q) return inTab;
      return inTab.filter(c => this.caseMatchesQuery(c, q));
    },

    // CompanyNumber of the agency the wizard is creating cases
    // for. Pinned to A-Affordable (1008) for now, since 1008 is
    // the only tenant with a seeded document library. When other
    // agencies have libraries set up via the authoring tool, this
    // can switch back to:
    //     this.$store.getters.getSelectedCompany?.companyNumber
    agencyCompanyNumber() {
      return 1008;
    },

    linkLanguageOptions() {
      return [
        { code: 'en-US', label: 'English' },
        { code: 'es-MX', label: 'Español' },
        { code: 'ru-RU', label: 'Русский' },
      ];
    },
  },

  methods: {
    // ── Case/participant shape helpers ──────────────────────────────
    // The Mongo case document uses participants.defendant,
    // participants.indemnitor1, participants.indemnitor2 (named keys)
    // and identity.first_name etc. These helpers keep the template
    // readable by abstracting the access paths.

    getDefendant(c) {
      return c.participants?.defendant || null;
    },
    getIndemnitors(c) {
      return [c.participants?.indemnitor1, c.participants?.indemnitor2].filter(Boolean);
    },
    caseDisplayId(c) {
      return c.case_fields?.court_case_number || '';
    },
    caseBondAmount(c) {
      return c.case_fields?.bond_amount_numeric || null;
    },
    caseUpdatedAt(c) {
      return c.updated_at ? new Date(c.updated_at).getTime() : 0;
    },
    caseCreatedAt(c) {
      return c.created_at ? new Date(c.created_at).getTime() : 0;
    },

    // Search-match: case number, participant names, court.
    caseMatchesQuery(c, q) {
      const d = this.getDefendant(c);
      const haystack = [
        c.case_fields?.court_case_number,
        d?.identity?.first_name,
        d?.identity?.last_name,
        c.case_fields?.court,
        ...this.getIndemnitors(c).flatMap(i => [
          i.identity?.first_name, i.identity?.last_name,
        ]),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    },

    // Compose a participant's display name from identity.
    displayName(p) {
      if (!p) return '';
      const id = p.identity || p; // fallback for legacy shape
      const parts = [id.first_name || id.firstName, id.last_name || id.lastName].filter(Boolean);
      return parts.join(' ');
    },

    // Short phone display for inline use in the participant line.
    formatPhoneShort(stored) {
      if (!stored) return '';
      if (stored.startsWith('+1') && stored.length === 12) {
        const d = stored.slice(2);
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
      }
      return stored;
    },

    // Progress summary for a participant, shown beside their name on
    // the dashboard row. Reads the server-computed .progress object
    // (populated by deriveForm in the GET /cases response). Returns
    // a string like "3/9 Sections · 41/152 Fields" or empty if no
    // progress data is available.
    progressSummary(p) {
      if (!p?.progress) return '';
      const s = p.progress.sections;
      const f = p.progress.fields;
      if (!s || !f || f.total === 0) return '';
      return `${s.done}/${s.total} Sections · ${f.done}/${f.total} Fields`;
    },

    // Action-oriented status string with color.
    derivedStatus(c) {
      return derivedStatusFor(c);
    },

    // Stuck = no activity in last 24 hours, and case is still active.
    isStuck(c) {
      if (c.status === 'archived' || c.status === 'completed') return false;
      return (now() - this.caseUpdatedAt(c)) > DAY;
    },

    // Format currency as US dollars without trailing decimals.
    formatCurrency(amount) {
      if (amount == null) return '';
      return amount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },

    // Format "x ago" for last activity. Handles ISO date strings
    // (from the API) and numbers (legacy).
    formatTimeAgo(timestamp) {
      if (!timestamp) return '';
      const t = typeof timestamp === 'number'
        ? timestamp
        : new Date(timestamp).getTime();
      const diff = now() - t;
      if (diff < 60 * 1000) return 'just now';
      if (diff < HOUR) return `${Math.floor(diff / (60 * 1000))}m ago`;
      if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
      if (diff < 2 * DAY) return 'yesterday';
      return `${Math.floor(diff / DAY)}d ago`;
    },

    // ── Event handlers (stubs for next pass) ─────────────────────────

    // Authenticated fetch helper. Gets a fresh Firebase token and
    // makes the request via native fetch (not axios), bypassing
    // any axios interceptors that might interfere with the header.
    // Returns the parsed JSON response body, or throws.
    // For FormData bodies, omits Content-Type so the browser sets
    // the multipart boundary automatically.
    async authedFetch(url, options = {}) {
      const user = auth.currentUser
        || await new Promise((resolve) => {
          const unsub = auth.onAuthStateChanged((u) => {
            unsub();
            resolve(u);
          });
        });
      if (!user) throw new Error('No authenticated user');
      const token = await user.getIdToken();
      const isFormData = typeof FormData !== 'undefined'
        && options.body instanceof FormData;
      const headers = {
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      };
      if (!isFormData) headers['Content-Type'] = 'application/json';
      const resp = await fetch(url, {
        ...options,
        headers,
      });
      return resp.json();
    },

    // Load all cases from the server. Called once on mount.
    async fetchCases() {
      try {
        const qs = `companyNumber=${this.smsCompanyNumber}`;
        const data = await this.authedFetch(`/portaldb/db/bbas/cases?${qs}`);
        if (data && data.success) {
          this.cases = data.cases || [];
        } else {
          console.warn('[Forms] cases fetch non-success:', data);
        }
      } catch (err) {
        console.error('[Forms] cases fetch failed:', err);
      }
    },

    onNewCaseClick() {
      // Ensure the wizard opens in create mode, not edit mode left
      // over from a prior edit session.
      this.editingCase = null;
      this.newCaseModalOpen = true;
    },

    // Open the wizard in edit mode for the given case. Fired from
    // click handlers on participant name / phone / email in the
    // dashboard row. The wizard lands on step 3 (People) and the
    // step-4 button reads "Save case" instead of "Create case".
    openEdit(c) {
      this.editingCase = c;
      this.newCaseModalOpen = true;
    },

    async onCaseCreated(payload) {
      try {
        const data = await this.authedFetch('/portaldb/db/bbas/cases', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        if (data && data.success) {
          this.cases.push(data.case);
          const d = this.getDefendant(data.case);
          const name = this.displayName(d);
          const id = this.caseDisplayId(data.case);
          this.createdSnackbarText = name
            ? `Created case for ${name}`
            : id
              ? `Created case ${id}`
              : 'Created new case';
          this.createdSnackbarOpen = true;
        } else {
          console.warn('[Forms] Case create rejected:', data?.message);
        }
      } catch (err) {
        console.error('[Forms] Case create error:', err);
      }
    },

    // Handles the wizard's @save event (edit mode). The payload
    // includes _id plus the mutable fields. PATCH the server, then
    // replace the local case wholesale with the response (server is
    // the source of truth — it bumps updated_at, normalizes the
    // participants shape, etc.).
    async onCaseUpdated(payload) {
      const caseId = payload._id;
      if (!caseId) {
        console.warn('[Forms] onCaseUpdated called without _id');
        return;
      }
      try {
        const body = { ...payload };
        delete body._id;
        const data = await this.authedFetch(
          `/portaldb/db/bbas/cases/${caseId}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
          }
        );
        if (data && data.success) {
          const idx = this.cases.findIndex(
            c => String(c._id) === String(caseId)
          );
          if (idx >= 0) this.cases.splice(idx, 1, data.case);

          const d = this.getDefendant(data.case);
          const name = this.displayName(d);
          this.createdSnackbarText = name
            ? `Updated case for ${name}`
            : this.caseDisplayId(data.case)
              ? `Updated case ${this.caseDisplayId(data.case)}`
              : 'Updated case';
          this.createdSnackbarOpen = true;
        } else {
          console.warn('[Forms] Case update rejected:', data?.message);
        }
      } catch (err) {
        console.error('[Forms] Case update error:', err);
      }
    },

    // Row expansion (inline detail view in the dashboard). Agents may
    // have multiple cases expanded at once — they manage their own
    // workspace.
    isExpanded(c) {
      return this.expandedCaseIds.has(c._id);
    },

    toggleExpansion(c) {
      // Vue 3 reactivity doesn't track Set mutations directly, so we
      // reassign the set after mutating it.
      const next = new Set(this.expandedCaseIds);
      if (next.has(c._id)) {
        next.delete(c._id);
      } else {
        next.add(c._id);
      }
      this.expandedCaseIds = next;
      // Close any open progress panel — the expanded detail view
      // replaces it as the primary info surface.
      this.activeProgress = null;
    },

    // ── Progress panel (inline in row header) ─────────────────────

    toggleProgress(c, role) {
      if (this.activeProgress
          && this.activeProgress.caseId === c._id
          && this.activeProgress.role === role) {
        this.activeProgress = null;
      } else {
        this.activeProgress = { caseId: c._id, role };
      }
    },

    isProgressActive(c, role) {
      return this.activeProgress
          && this.activeProgress.caseId === c._id
          && this.activeProgress.role === role;
    },

    // ── Archive / unarchive ───────────────────────────────────────

    async onArchiveToggle(c) {
      const newStatus = c.status === 'archived' ? 'active' : 'archived';
      // Optimistic local update.
      const oldStatus = c.status;
      c.status = newStatus;
      try {
        const data = await this.authedFetch(
          `/portaldb/db/bbas/cases/${c._id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: newStatus }),
          }
        );
        if (data && data.success) {
          // Replace with server response (has updated_at, etc.)
          const idx = this.cases.findIndex(
            cs => String(cs._id) === String(c._id)
          );
          if (idx >= 0) this.cases.splice(idx, 1, data.case);
        } else {
          // Revert on failure.
          c.status = oldStatus;
          console.warn('[Forms] Archive toggle rejected:', data?.message);
        }
      } catch (err) {
        c.status = oldStatus;
        console.error('[Forms] Archive toggle error:', err);
      }
    },

    // "Open full view" link in the expanded row opens the full-page
    // CaseDetail view (which has room for case fields, document
    // management, sealing, etc).
    openFullView(c) {
      this.selectedCase = c;
    },

    // ── Per-participant message strip expansion ──────────────────
    // Each strip is keyed by case + role [+ idx for indemnitors].
    messageKey(c, role, idx) {
      let k = `${c._id}:${role}`;
      if (idx !== undefined && idx !== null) k += `:${idx}`;
      return k;
    },

    isMessageExpanded(c, role, idx) {
      return this.expandedMessageKeys.has(this.messageKey(c, role, idx));
    },

    toggleMessageExpansion(c, role, idx) {
      const key = this.messageKey(c, role, idx);
      const next = new Set(this.expandedMessageKeys);
      const expanding = !next.has(key);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      this.expandedMessageKeys = next;

      // On expand: (1) make sure the thread is loaded so the panel
      // renders messages, and (2) mark any inbound as read. The
      // acknowledge fires whether or not the thread is already in
      // cache — for bare-chrome panels the SmsThreadPanel doesn't
      // self-trigger this the way it does for full-chrome
      // (toggleUnfurl emits 'acknowledge'), so Forms.vue handles it
      // here at the orchestration level.
      if (expanding) {
        const phone = this._participantPhone(c, role, idx);
        if (phone) {
          this.ensureThreadLoaded(phone);
          this.onMessageAcknowledge({ phone });
        }
      }
    },

    // Look up which participant a (case, role, idx) refers to. Used
    // by toggleMessageExpansion to find the phone to fetch.
    _participantPhone(c, role, idx) {
      if (role === 'defendant') return this.getDefendant(c)?.identity?.phone || null;
      if (role === 'indemnitor') return this.getIndemnitors(c)?.[idx ?? 0]?.identity?.phone || null;
      return null;
    },

    // Total unread (unacknowledged inbound) message count for a case,
    // summed across all participants. Used to render the red badge
    // on the row header so the agent sees activity without
    // expanding. Reads from the phone-keyed thread cache; for phones
    // whose threads haven't loaded yet, the count is 0 (the badge
    // appears once the agent expands and the GET completes).
    caseUnreadCount(c) {
      const fromPhone = (phone) => {
        if (!phone) return 0;
        const entry = this.threadsByPhone[phone];
        if (!entry || !entry.thread) return 0;
        return entry.thread.filter(
          m => m.type === 'inbound' && m.acknowledged === false
        ).length;
      };
      let total = fromPhone(this.getDefendant(c)?.identity?.phone);
      for (const indem of this.getIndemnitors(c) || []) total += fromPhone(indem.identity?.phone);
      return total;
    },

    // ── SMS thread cache access ──────────────────────────────────

    // Returns the thread array for a given phone (or empty array if
    // not yet cached). ParticipantMessages binds to this.
    getThread(phone) {
      if (!phone) return [];
      return this.threadsByPhone[phone]?.thread || [];
    },

    // Returns the consent record for a given phone. In BBAS the
    // consent gate is bypassed at the SmsThreadPanel level
    // (requires-consent=false), so this is effectively unused —
    // we still return the cached value (or a synthetic 'granted')
    // so the prop has a sensible shape.
    getConsent(phone) {
      if (!phone) return { status: 'granted', statusAt: null };
      return this.consentByPhone[phone] || { status: 'granted', statusAt: null };
    },

    isSending(phone) {
      return phone ? this.sendingPhones.has(phone) : false;
    },

    // Template-ref callback wired via :ref="..." on each
    // ParticipantMessages. Stores the component instance under its
    // participant's phone so we can call clearComposer() on the
    // right instance after a successful send. Null when the element
    // is being unmounted.
    registerMessagesRef(phone, el) {
      if (!phone) return;
      if (el) this.messagesRefs[phone] = el;
      else delete this.messagesRefs[phone];
    },

    // ── SMS API calls ───────────────────────────────────────────

    // Load this phone's thread if we don't already have it. Idempotent
    // — concurrent calls (e.g., two strips expand simultaneously)
    // result in only one GET because we mark loaded=true while in
    // flight via the entry's presence.
    async ensureThreadLoaded(phone) {
      if (!phone) return;
      const existing = this.threadsByPhone[phone];
      if (existing && existing.loaded) return;
      if (existing && existing.fetching) return;

      // Mark as fetching so a second simultaneous trigger doesn't
      // double-fetch. Initialize thread as empty so the strip renders.
      this.threadsByPhone = {
        ...this.threadsByPhone,
        [phone]: { thread: [], loaded: false, fetching: true },
      };

      try {
        const qs = `phone=${encodeURIComponent(phone)}&companyNumber=${this.smsCompanyNumber}`;
        const data = await this.authedFetch(`/portaldb/db/sms/thread?${qs}`);
        if (data && data.success) {
          this.threadsByPhone = {
            ...this.threadsByPhone,
            [phone]: { thread: data.thread || [], loaded: true, fetching: false },
          };
          this.consentByPhone = {
            ...this.consentByPhone,
            [phone]: data.consent || { status: 'granted', statusAt: null },
          };
        } else {
          console.warn('[Forms] sms/thread non-success:', data);
          this.threadsByPhone[phone].fetching = false;
        }
      } catch (err) {
        console.error('[Forms] sms/thread failed for', phone, err);
        if (this.threadsByPhone[phone]) {
          this.threadsByPhone[phone].fetching = false;
        }
      }
    },

    // SmsThreadPanel.@send → ParticipantMessages.@send → here.
    // Posts to /portaldb/db/sms/send with sendWithoutConsent: true so
    // the server-side consent gate is bypassed. The outbound message
    // arrives back via the smsMessages SSE event (we don't append
    // optimistically — see VoiceMailRecord's rationale: avoids
    // double-render once SSE delivers the same record).
    async onMessageSend(participant, { body, attachment }) {
      const phone = participant?.identity?.phone;
      if (!phone) {
        console.warn('[Forms] cannot send — participant has no phone');
        return;
      }

      // Mark as sending so the panel's send button disables.
      const nextSending = new Set(this.sendingPhones);
      nextSending.add(phone);
      this.sendingPhones = nextSending;

      try {
        const fd = new FormData();
        fd.append('to', phone);
        fd.append('companyNumber', String(this.smsCompanyNumber));
        fd.append('sendWithoutConsent', 'true');
        if (body) fd.append('body', body);
        if (attachment) fd.append('image', attachment);

        const data = await this.authedFetch('/portaldb/db/sms/send', {
          method: 'POST',
          body: fd,
        });

        if (data && data.success) {
          this.messagesRefs[phone]?.clearComposer();
          // SSE will deliver the outbound record; nothing more to do here.
        } else {
          const reason = (data && (data.reason || data.message)) || 'unknown';
          console.warn('[Forms] SMS send blocked:', reason, data);
          // TODO: surface to user (snackbar). Reasons we'd actually
          // see: twilio_error, bad_recipient, bad_body, config_error.
          // consent_required shouldn't fire — we set
          // sendWithoutConsent: true — but log it loudly if it does.
        }
      } catch (err) {
        console.error('[Forms] SMS send failed:', err);
      } finally {
        const next = new Set(this.sendingPhones);
        next.delete(phone);
        this.sendingPhones = next;
      }
    },

    // SmsThreadPanel.@acknowledge → here. Optimistic local update
    // first (every unacked inbound becomes acknowledged immediately),
    // then fire-and-forget POST. If the POST fails we don't roll back
    // — next thread GET will reconcile. Called both on @acknowledge
    // and (in BBAS) directly from toggleMessageExpansion since
    // bare-chrome SmsThreadPanels don't self-trigger acknowledge.
    async onMessageAcknowledge(participant) {
      const phone = participant?.identity?.phone || participant?.phone;
      if (!phone) return;

      // Optimistic local mark-read if we already have the thread.
      // (When called from toggleMessageExpansion before the thread
      // has loaded, this branch skips — the GET that's running in
      // parallel will return server state with messages either
      // still-unack'd, or ack'd if our POST landed first. Either is
      // fine; the badge converges.)
      const entry = this.threadsByPhone[phone];
      if (entry && entry.thread && entry.thread.length > 0) {
        const updatedThread = entry.thread.map((m) =>
          m.type === 'inbound' && !m.acknowledged
            ? { ...m, acknowledged: true }
            : m
        );
        this.threadsByPhone = {
          ...this.threadsByPhone,
          [phone]: { ...entry, thread: updatedThread },
        };
      }

      try {
        await this.authedFetch('/portaldb/db/sms/acknowledge', {
          method: 'POST',
          body: JSON.stringify({
            phone,
            companyNumber: this.smsCompanyNumber,
          }),
        });
      } catch (err) {
        console.error('[Forms] sms/acknowledge failed for', phone, err);
        // Don't roll back — best-effort. Next GET reconciles.
      }
    },

    // ── SSE subscription ────────────────────────────────────────

    subscribeToSmsEvents() {
      const cn = this.smsCompanyNumber;
      if (!cn) return;
      const events = eventService.getConnection(cn);
      if (!events) return;
      if (this.smsMessageHandler) return; // idempotent

      this.smsMessageHandler = (event) => this.handleSmsMessageEvent(event);
      events.addEventListener('smsMessages', this.smsMessageHandler);
    },

    unsubscribeFromSmsEvents() {
      if (!this.smsMessageHandler) return;
      const cn = this.smsCompanyNumber;
      if (!cn) return;
      const events = eventService.getConnection(cn);
      if (!events) {
        this.smsMessageHandler = null;
        return;
      }
      events.removeEventListener('smsMessages', this.smsMessageHandler);
      this.smsMessageHandler = null;
    },

    // ── Case change SSE ──────────────────────────────────────────
    //
    // The SSEManager watches bbas.cases via a Mongo Change Stream
    // and emits 'cases' events to connected clients. When we get
    // one, we debounce a full fetchCases() call so the dashboard
    // reflects the change (new cases, status updates, participant
    // data changes from the SPA, etc.). The refetch also recomputes
    // progress counts server-side.

    subscribeToCaseEvents() {
      const cn = this.smsCompanyNumber;
      if (!cn) return;
      const events = eventService.getConnection(cn);
      if (!events) return;
      if (this.caseEventHandler) return;

      this.caseEventHandler = () => {
        // Debounce: collapse rapid-fire changes into one refetch.
        if (this.caseRefetchTimer) clearTimeout(this.caseRefetchTimer);
        this.caseRefetchTimer = setTimeout(() => {
          this.fetchCases();
        }, 500);
      };
      events.addEventListener('cases', this.caseEventHandler);
    },

    unsubscribeFromCaseEvents() {
      if (this.caseRefetchTimer) {
        clearTimeout(this.caseRefetchTimer);
        this.caseRefetchTimer = null;
      }
      if (!this.caseEventHandler) return;
      const cn = this.smsCompanyNumber;
      if (!cn) return;
      const events = eventService.getConnection(cn);
      if (!events) {
        this.caseEventHandler = null;
        return;
      }
      events.removeEventListener('cases', this.caseEventHandler);
      this.caseEventHandler = null;
    },

    // SSE 'smsMessages' handler. Dispatches the incoming message to
    // the right phone-keyed thread, with the same filter logic
    // VoiceMailRecord uses (from === phone | to === phone |
    // related_to === phone). Three cases for the cache entry:
    //
    //   - loaded:      append (with messageSid de-dup)
    //   - fetching:    drop — the GET response will include the row
    //   - missing:     for inbound events on phones we care about
    //                  (i.e., associated with a case on the
    //                  dashboard), trigger ensureThreadLoaded so the
    //                  unread badge updates. Otherwise the agent
    //                  wouldn't see new inbound until they expanded
    //                  the strip themselves. For outbound/system on
    //                  unloaded phones, drop — those don't drive any
    //                  collapsed-state UI.
    handleSmsMessageEvent(event) {
      let msg;
      try { msg = JSON.parse(event.data); }
      catch (e) { return; }

      // Determine which phone this message belongs to.
      let phone;
      if (msg.direction === 'inbound')        phone = msg.from;
      else if (msg.direction === 'outbound')  phone = msg.to;
      else if (msg.direction === 'system')    phone = msg.related_to;
      if (!phone) return;

      const entry = this.threadsByPhone[phone];

      if (!entry || !entry.loaded) {
        if (entry?.fetching) return;
        // Unloaded phone. Trigger a fetch only for inbound (the
        // direction that drives badges) and only if the phone is
        // actually on the dashboard — otherwise SSE noise from
        // unrelated company SMS (e.g. voicemail-driven) would cause
        // useless API calls.
        if (msg.direction === 'inbound' && this.isPhoneInDashboard(phone)) {
          this.ensureThreadLoaded(phone);
        }
        return;
      }

      // De-dup: SSE may deliver an update event for an already-seen
      // record, and the GET response may include a record that
      // arrived between mount and first unfurl.
      if (entry.thread.find((m) => m.id === msg.messageSid)) return;

      let mapped;
      if (msg.direction === 'system') {
        mapped = {
          id: msg.messageSid,
          type: 'system',
          body: msg.body || '',
          severity: msg.severity || 'info',
          reason: msg.reason || null,
          attemptedBy: msg.attempted_by || null,
          at: msg.sentAt || null,
        };
      } else {
        const at = (msg.direction === 'inbound' ? msg.receivedAt : msg.sentAt)
                || msg.receivedTimestamp
                || null;
        mapped = {
          id: msg.messageSid,
          type: msg.direction === 'inbound' ? 'inbound' : 'outbound',
          body: msg.body || '',
          at,
          sentBy: msg.sent_by || null,
          mediaUrls: Array.isArray(msg.mediaUrls) ? msg.mediaUrls : [],
          acknowledged: !!msg.acknowledged_at,
        };
      }

      const nextThread = [...entry.thread, mapped].sort((a, b) => {
        if (!a.at) return 1;
        if (!b.at) return -1;
        return new Date(a.at) - new Date(b.at);
      });

      this.threadsByPhone = {
        ...this.threadsByPhone,
        [phone]: { ...entry, thread: nextThread },
      };
    },

    // Whether the given phone is associated with any participant on
    // any currently-loaded case. Used to filter SSE noise — we don't
    // want to fetch threads for phones that have nothing to do with
    // the BBAS dashboard.
    isPhoneInDashboard(phone) {
      if (!phone) return false;
      for (const c of this.cases) {
        if (this.getDefendant(c)?.identity?.phone === phone) return true;
        for (const indem of this.getIndemnitors(c) || []) {
          if (indem.identity?.phone === phone) return true;
        }
      }
      return false;
    },

    // ── Send magic link ─────────────────────────────────────────

    onSendLink(c, role, method) {
      const participant = c.participants?.[role];
      if (!participant) return;
      const id = participant.identity || {};
      const name = [id.first_name, id.last_name].filter(Boolean).join(' ')
        || (role === 'defendant' ? 'Defendant' : 'Indemnitor');
      let contact;
      if (method === 'sms') {
        contact = this.formatPhoneShort(id.phone) || id.phone || '';
      } else {
        contact = id.email || '';
      }
      this.sendLinkTarget = {
        caseId: c._id,
        role,
        method,
        name,
        contact,
      };
      this.sendLinkLocale = 'en-US';
      this.sendLinkSubmitting = false;
      this.sendLinkDialogOpen = true;
    },

    async onSendLinkConfirm() {
      const { caseId, role, method } = this.sendLinkTarget;
      if (!caseId || !role || !method) return;
      this.sendLinkSubmitting = true;
      try {
        const data = await this.authedFetch(
          `/portaldb/db/bbas/cases/${caseId}/send-link`, {
            method: 'POST',
            body: JSON.stringify({
              role,
              method,
              locale: this.sendLinkLocale,
            }),
          }
        );
        if (data && data.success) {
          const idx = this.cases.findIndex(
            cs => String(cs._id) === String(caseId)
          );
          if (idx >= 0) this.cases.splice(idx, 1, data.case);

          this.sendLinkDialogOpen = false;
          if (data.sent) {
            this.createdSnackbarText = `Link sent via ${method}`;
          } else {
            this.createdSnackbarText = `Link created but delivery failed: ${data.sendError || 'unknown'}`;
          }
          this.createdSnackbarOpen = true;
        } else {
          console.warn('[Forms] send-link rejected:', data?.message);
          alert(data?.message || 'Failed to send link');
        }
      } catch (err) {
        console.error('[Forms] send-link error:', err);
        alert('Failed to send link');
      } finally {
        this.sendLinkSubmitting = false;
      }
    },

    // Participant action passthrough — stub behavior for actions
    // not yet implemented.
    onParticipantAction(c, actionType, payload = {}) {
      console.log('[Forms] participant action:', actionType, payload, 'on case', c._id);
      const role = payload.role || 'participant';
      const messages = {
        view_form:         `View ${role}'s form — coming next pass`,
        ok_to_sign:        `Mark ${role} OK-to-sign — coming next pass`,
        view_docs:         `View ${role}'s signed documents — coming next pass`,
        remove_indemnitor: 'Remove indemnitor — coming next pass',
      };
      alert(messages[actionType] || `Action: ${actionType}`);
    },

    onCaseUpdate(updatedCase) {
      // The case detail view emits 'update' when something on the case
      // changes (rare for this pass — most actions are stubs). Replace
      // the case in the list.
      const idx = this.cases.findIndex(c => String(c._id) === String(updatedCase._id));
      if (idx >= 0) {
        this.cases.splice(idx, 1, updatedCase);
        this.selectedCase = updatedCase;
      }
    },
  },

  mounted() {
    this.fetchCases();
    this.subscribeToSmsEvents();
    this.subscribeToCaseEvents();
  },

  beforeUnmount() {
    this.unsubscribeFromSmsEvents();
    this.unsubscribeFromCaseEvents();
  },
};
</script>


<style scoped>
.forms-container {
  /* Override Vuetify's default fluid container width — we want a wider
     workspace for the dashboard so participant names don't wrap. */
  max-width: none;
  padding: 20px;
}

/* ── Header row ──────────────────────────────────────────────── */
.header-row {
  gap: 16px;
  margin-bottom: 16px;
}
.title-col h1 {
  font-size: 1.5em;
  margin: 0;
  color: #635347;
}
.tab-count {
  margin-left: 6px;
  font-size: 0.85em;
  color: #888;
}

/* ── Search row ──────────────────────────────────────────────── */
.search-row {
  margin-bottom: 16px;
}

/* ── Case card ───────────────────────────────────────────────── */
.case-card {
  margin-bottom: 8px;
  transition: background-color 0.15s ease;
  background-color: #fdfcf7;
}
.case-card:hover {
  background-color: #f9f7f3;
}
.case-card-expanded {
  /* Stays in hover-look state while expanded — no visual jolt on
     click. Agent's eye keeps its anchor on the row header. */
  background-color: #f9f7f3;
}
.case-card-stuck {
  border-left: 3px solid #ef6c00 !important;
}

.case-row {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 16px;
  cursor: pointer;
}

/* ── Expanded content ────────────────────────────────────────── */
.case-expanded {
  /* Fixed height with internal scroll. Agents can expand multiple rows
     and see them as parallel workspaces; if a single case has more
     content than fits, the scroll is internal to that row. */
  height: 400px;
  overflow-y: auto;
  padding: 16px 24px 20px 24px;
  border-top: 1px solid #e8e4dc;
  cursor: default;
}
.expanded-section {
  margin-bottom: 20px;
}
.expanded-section:last-child {
  margin-bottom: 0;
}
.expanded-section-title {
  font-size: 0.95em;
  font-weight: 500;
  color: #635347;
  margin: 0 0 10px 0;
}
.expanded-notes {
  padding: 12px 16px;
  background-color: #fff;
  border: 1px solid #e8e4dc;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  white-space: pre-wrap;
}
.expanded-stub {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background-color: #fff;
  border: 1px dashed #e8e4dc;
  border-radius: 6px;
  color: #888;
  font-size: 13px;
  font-style: italic;
}

/* Case ID column */
.col-id {
  flex: 0 0 110px;
}
.id-value {
  font-size: 14px;
  font-weight: 600;
  color: #635347;
}
.id-missing {
  color: #aaa;
}
.id-hint {
  font-size: 11px;
  color: #aaa;
  margin-top: 2px;
}

/* Participants column (defendant + indemnitors, stacked) */
.col-participants {
  flex: 1 1 0;
  min-width: 400px;
  overflow: hidden;
}
.participant-line {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  line-height: 1.5;
  color: #222;
  flex-wrap: wrap;
  /* Hanging indent: wrapped lines start at 50px from the left
     edge of the participant column. The first child (icon/tooltip)
     is pulled back so the first line starts at the true left edge. */
  padding-left: 30px;
}
.participant-line > :first-child {
  margin-left: -30px;
}
.participant-line + .participant-line {
  margin-top: 1px;
}
.participant-icon-defendant {
  color: #635347;
}
.participant-icon-indemnitor {
  color: #d2b48c;
}
.participant-name {
  color: #222;
  font-weight: normal;
}
.participant-progress {
  font-size: 12px;
  color: rgb(160, 50, 0);
  font-weight: normal;
  margin-left: 4px;
  white-space: nowrap;
}
.participant-contact {
  font-size: 12px;
  color: #222;
  font-weight: normal;
  margin-left: 4px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.participant-contact.contact-missing {
  color: #bbb;
  font-style: italic;
}

/* Click targets that open the case wizard in edit mode for this
   case. Applied to participant name, phone, and email spans. The
   subtle hover affordance (underline + brown color shift) tells
   the agent these are tappable; cursor: pointer reinforces it.
   The hover treatment is intentionally restrained — these spans
   are densely packed on each row, so anything too loud would
   make the dashboard noisy at rest. */
.clickable-edit {
  cursor: pointer;
  transition: color 100ms;
}
.clickable-edit:hover {
  color: #635347;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}
.clickable-edit.contact-missing:hover {
  color: #635347;
  font-style: italic;
}
.clickable-progress {
  cursor: pointer;
  transition: color 100ms;
}
.clickable-progress:hover {
  color: #635347;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}
.contact-icon {
  color: #aaa;
  margin-left: 2px;
  margin-right: 1px;
  vertical-align: middle;
}
.court-line {
  font-size: 12px;
  color: #888;
  margin-top: 4px;
  margin-left: 22px; /* aligns under participant names, past the icon */
}

/* Bond amount column */
.col-bond {
  flex: 0 0 100px;
  text-align: right;
}
.bond-value {
  font-size: 14px;
  font-weight: 500;
  color: #222;
}
.bond-missing {
  color: #aaa;
}

/* Status column */
.col-status {
  flex: 0 0 154px;
  /* Anchor for the absolutely-positioned status-detail */
  position: relative;
}
.status-chip {
  font-weight: 500;
  /* Same-width chips: all status labels align in a column for less
     visual noise. Width tuned to fit the longest label
     ("Awaiting signatures") with comfortable padding. */
  width: 134px;
  justify-content: center;
}
.status-detail {
  /* Float below the chip without affecting the column's height. This
     keeps the bond amount in the column to the left vertically
     centered against the chip itself, not against the chip+detail
     block. */
  position: absolute;
  left: 0;
  top: calc(50% + 14px);
  width: 134px;
  text-align: center;
  font-size: 11px;
  color: #888;
}

/* Last activity column */
.col-activity {
  flex: 0 0 100px;
  text-align: right;
  font-size: 12px;
  color: #888;
}
.activity-time {
  font-weight: 500;
}
.row-unread {
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  font-size: 11px;
}
.row-unread-label {
  color: #d32f2f;
  font-weight: 500;
}
.stuck-indicator {
  margin-top: 4px;
  color: #ef6c00;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

/* Kebab menu column */
.col-kebab {
  flex: 0 0 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kebab-btn {
  color: #aaa;
}
.kebab-btn:hover {
  color: #635347;
}
.kebab-list {
  min-width: 120px;
}

/* ── Empty state ─────────────────────────────────────────────── */
.empty-state {
  padding: 60px 20px;
  text-align: center;
}
.empty-state-text {
  margin-top: 12px;
  color: #999;
  font-size: 14px;
}

/* ── Mobile: stack the row ───────────────────────────────────── */
@media (max-width: 768px) {
  .case-row {
    flex-wrap: wrap;
    row-gap: 8px;
  }
  .col-id {
    flex: 0 0 auto;
  }
  .col-defendant,
  .col-participants {
    flex: 1 1 auto;
  }
  .col-bond {
    flex: 0 0 auto;
    text-align: left;
  }
  .col-status {
    flex: 1 1 100%;
  }
  .col-activity {
    flex: 0 0 auto;
    text-align: left;
  }
  .stuck-indicator {
    justify-content: flex-start;
  }
}
/* ── Send Link dialog ───────────────────────────────────────── */
.dialog-title {
  font-size: 1.125rem;
  font-weight: 500;
  color: #635347;
  padding: 16px 20px 8px 20px;
}
.dialog-body {
  padding: 8px 20px 12px 20px;
}
.send-link-prompt {
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}
.lang-option-btn {
  text-transform: none;
  letter-spacing: 0;
  min-width: 64px;
}
</style>
